import { useState, useEffect } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { X, Users, Star, Clock, Calendar, Wallet, Mail } from "lucide-react";
import { CustomerFilters as CustomerFiltersType } from "@/types/customer";

export type CustomerFilters = CustomerFiltersType;

const defaultFilters: CustomerFilters = {
  registrationStatus: 'all',
  loyaltyTier: [],
  orderFrequency: 'all',
  lastOrderDays: [0, 365],
  spendRange: [0, 10000],
  marketingOptIn: null,
};

interface CustomerFiltersProps {
  onChange: (filters: CustomerFilters) => void;
  onReset: () => void;
  initialFilters?: Partial<CustomerFilters>;
}

export const CustomerFilters = ({ 
  onChange,
  onReset,
  initialFilters = {}
}: CustomerFiltersProps) => {
  const [filters, setFilters] = useState<CustomerFilters>({
    ...defaultFilters,
    ...initialFilters
  });
  
  const getActiveFilterCount = (): number => {
    let count = 0;
    
    if (filters.registrationStatus !== 'all') count++;
    if (filters.loyaltyTier.length > 0) count++;
    if (filters.orderFrequency !== 'all') count++;
    if (filters.lastOrderDays[0] > 0 || filters.lastOrderDays[1] < 365) count++;
    if (filters.spendRange[0] > 0 || filters.spendRange[1] < 10000) count++;
    if (filters.marketingOptIn !== null) count++;
    
    return count;
  };
  
  const updateFilters = <K extends keyof CustomerFilters>(
    key: K,
    value: CustomerFilters[K]
  ) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onChange(newFilters);
  };
  
  const handleReset = () => {
    setFilters(defaultFilters);
    onReset();
  };
  
  const loyaltyTiers = [
    { id: 'bronze', label: 'Bronze' },
    { id: 'silver', label: 'Silver' },
    { id: 'gold', label: 'Gold' },
    { id: 'platinum', label: 'Platinum' }
  ];
  
  return (
    <Card className="mb-4">
      <CardContent className="pt-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">Filter Customers</h3>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleReset}
            className="gap-2"
          >
            <X className="h-4 w-4" />
            Reset
            {getActiveFilterCount() > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 w-5 rounded-full p-0 flex items-center justify-center">
                {getActiveFilterCount()}
              </Badge>
            )}
          </Button>
        </div>
        
        <div className="space-y-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              <Label className="text-sm font-medium">Registration Status</Label>
            </div>
            <RadioGroup
              value={filters.registrationStatus}
              onValueChange={(value) => 
                updateFilters('registrationStatus', value as CustomerFilters['registrationStatus'])
              }
              className="flex flex-wrap gap-3"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="all" id="all-reg" />
                <Label htmlFor="all-reg">All</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="registered" id="registered" />
                <Label htmlFor="registered">Registered</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="not_registered" id="not-registered" />
                <Label htmlFor="not-registered">Not Registered</Label>
              </div>
            </RadioGroup>
          </div>
          
          <Separator />
          
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Star className="h-4 w-4 text-muted-foreground" />
              <Label className="text-sm font-medium">Loyalty Tier</Label>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {loyaltyTiers.map((tier) => (
                <div key={tier.id} className="flex items-center space-x-2">
                  <Checkbox 
                    id={tier.id}
                    checked={filters.loyaltyTier.includes(tier.id)}
                    onCheckedChange={(checked) => {
                      const newTiers = checked
                        ? [...filters.loyaltyTier, tier.id]
                        : filters.loyaltyTier.filter(t => t !== tier.id);
                      updateFilters('loyaltyTier', newTiers);
                    }}
                  />
                  <Label htmlFor={tier.id}>{tier.label}</Label>
                </div>
              ))}
            </div>
          </div>
          
          <Separator />
          
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <Label className="text-sm font-medium">Order Frequency</Label>
            </div>
            <ToggleGroup
              type="single"
              variant="outline"
              value={filters.orderFrequency}
              onValueChange={(value) => {
                if (value) updateFilters('orderFrequency', value as CustomerFilters['orderFrequency']);
              }}
              className="justify-start flex-wrap"
            >
              <ToggleGroupItem value="all" aria-label="All order frequencies">
                All
              </ToggleGroupItem>
              <ToggleGroupItem value="frequent" aria-label="Frequent orders">
                Frequent
              </ToggleGroupItem>
              <ToggleGroupItem value="occasional" aria-label="Occasional orders">
                Occasional
              </ToggleGroupItem>
              <ToggleGroupItem value="oneTime" aria-label="One-time orders">
                One Time
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
          
          <Separator />
          
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <Label className="text-sm font-medium">Days Since Last Order</Label>
            </div>
            <div className="mt-4 mx-2 mb-6">
              <Slider
                defaultValue={filters.lastOrderDays}
                min={0}
                max={365}
                step={1}
                onValueChange={(value) => updateFilters('lastOrderDays', value as [number, number])}
              />
              <div className="flex justify-between mt-2 text-xs text-muted-foreground">
                <span>Today</span>
                <span>1 year</span>
              </div>
            </div>
            <div className="flex justify-between">
              <div className="text-sm">
                <span className="font-medium">{filters.lastOrderDays[0]}</span>
                <span className="text-muted-foreground"> to </span>
                <span className="font-medium">{filters.lastOrderDays[1]}</span>
                <span className="text-muted-foreground"> days</span>
              </div>
            </div>
          </div>
          
          <Separator />
          
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Wallet className="h-4 w-4 text-muted-foreground" />
              <Label className="text-sm font-medium">Total Spend Range</Label>
            </div>
            <div className="mt-4 mx-2 mb-6">
              <Slider
                defaultValue={filters.spendRange}
                min={0}
                max={10000}
                step={50}
                onValueChange={(value) => updateFilters('spendRange', value as [number, number])}
              />
              <div className="flex justify-between mt-2 text-xs text-muted-foreground">
                <span>$0</span>
                <span>$10,000+</span>
              </div>
            </div>
            <div className="flex justify-between">
              <div className="text-sm">
                <span className="font-medium">${filters.spendRange[0]}</span>
                <span className="text-muted-foreground"> to </span>
                <span className="font-medium">${filters.spendRange[1]}</span>
              </div>
            </div>
          </div>
          
          <Separator />
          
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <Label className="text-sm font-medium">Marketing Opt-In</Label>
            </div>
            <RadioGroup
              value={filters.marketingOptIn === null ? 'all' : filters.marketingOptIn ? 'opted-in' : 'opted-out'}
              onValueChange={(value) => {
                const optInValue = 
                  value === 'all' ? null :
                  value === 'opted-in' ? true : false;
                updateFilters('marketingOptIn', optInValue);
              }}
              className="flex flex-wrap gap-3"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="all" id="all-marketing" />
                <Label htmlFor="all-marketing">All</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="opted-in" id="opted-in" />
                <Label htmlFor="opted-in">Opted In</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="opted-out" id="opted-out" />
                <Label htmlFor="opted-out">Opted Out</Label>
              </div>
            </RadioGroup>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CustomerFilters;
